import { CarbonSavingsModel } from "../../../models/product";

export enum  ProductConfiguratorModuleType {
    ContentModule = 0,
    RadioButtonInputModule = 1,
    ImageInputModule = 2,
    TextInputModule = 3,
    ProductListModule = 4,
    ProductSummaryModule = 5,
    SelectedOptionsSummaryModule = 6,
    StraightWallSectionModule = 7,
    CurvedWallSectionModule = 8,
    BuildingOutdoorWallSectionModule = 9,
    BuildingIndoorWallSectionModule = 10,
    BathroomWallSectionModule = 11,
    ProductListCustomModule = 12,
    StepByStepInstructionsProductListModule = 13,
}

export interface UiState<T> {
    id: any;
    state: T;
}

export enum WallTypes {
    Undefined = 0,
    Dividing = 1,
    Retaining = 2
}

export interface PropertyModelValue {
    key: string;
    value: any;
}

export interface ConfiguratorValue {
    property: ConfiguratorProperty;
    value: any;
}

export interface ConfiguratorModuleValue extends ConfiguratorValue {
    id: string;
    valueDescriptions: ValueDescription[];
}

export interface ValueDescription {
    title: string;
    text: string;
}

export interface ConfiguratorValueUpdateModel {
    moduleId: string;
    value: any;
    valueDescriptions: ValueDescription[];
    propertyName: string;
    isText: boolean;
    resetClimateSavingsCookie: boolean;
}

export interface ProductConfiguratorModel {
    configuratorType: string;
    title: string;
    description: string;
    steps: Step[];
    products: Product[];
    propertyGroups: ConfiguratorPropertyGroup[];
    plasticBoardPercentage: number;
    securityMargin: number;
    enableShareConfigurator: number;
    materialConsumptionFactorOutdoorWall: number;
    materialConsumptionFactorIndoorWall: number;
    materialConsumptionFactorExaktOutdoorWall: number;
    cornerBlockOpeningBlockQuantityFactor: number;
    adjustmentFactorForNormalBlockPassPicker: number;
}

export interface ConfiguratorProperty {
    propertyName: string;
    description: string;
    mandatory: boolean;
    sortIndex: number;
    settingsProperty: boolean;
}

export interface ConfiguratorPropertyGroup {
    propertyGroupType: number;
    description: string;
    sortIndex: number;
    properties: ConfiguratorProperty[];
}

export interface Step {
    id: string;
    index: number;
    title: string;
    content: string;
    displayNextStepButton: boolean;
    sections: Section[];
}

export interface Section {
    id: string;
    index: number;
    title: string;
    content: string;
    hideDelimiter: boolean;
    modules: Module[];
    conditionalPropertyValues: ConditionalPropertyValue[];
}

export interface Module {
    options: any;
    id: string;
    index: number;
    title: string;
    moduleType: ProductConfiguratorModuleType;
    configuratorProperties: ConfiguratorProperty[];
    hideGableInput: boolean;
    configuratorType: string;
    desktopNumberOfImageOptions: number;
    mobileNumberOfImageOptions: number;
    isOnlyShoppingList: boolean;
    resetClimateSavingsCookie: boolean;
}

export interface ContentModuleModel extends Module {
    content: string;
    contentReadMore: string;
    backgroundColor: string;
    borderColor: string;
}

export interface ProductListModuleModel extends Module {
    products: Product[];
    autoAddProducts: boolean;
    hideQuantity: boolean;
    addOnlyOneProduct: boolean;
    showSelectText: boolean;
}

export interface StepByStepInstructionsProductListModuleModel extends Module {
    products: Product[];
}

export interface ProductListCustomModuleModel extends Module {
    customProducts: Product[];
    customProductsHeightOptions: VariantHeightOrWeightOptions[];
    customProductsWidthOptions: VariantHeightOrWeightOptions[];
    recommendedSectionProductFromCookie: string;
    selectedWidthFromCookie: number;
    selectedHeightFromCookie: number;
}

export interface InputModule extends Module {
}

export interface RadioButtonInputModuleModel<T> extends Module {
    value: T;
    options: Array<RadioButtonInputModuleOption<T>>;
}

export interface RadioButtonInputModuleOption<T> {
    title: string;
    description: string;
    value: T;
    preSelected: boolean;
}

export interface ImageInputModuleModel<T> extends Module {
    options: Array<ImageInputModuleOption<T>>;
}

export interface ImageInputModuleOption<T> {
    url: string;
    title: string;
    description: string;
    value: T;
    preSelected: boolean;
}

export interface TextInputModuleModel<T> extends Module {
    options: Array<TextInputModuleOption<T>>;
    listTextBoxHorizontally: boolean;
    backgroundColor: string;
}

export interface ProductSummaryModuleModel extends Module {
    products: Product[];
    cartUrl: string;
}

export interface SelectedOptionsSummaryModuleModel extends Module {
    enableShareConfigurator: boolean;
}

export interface TextInputModuleOption<T> {
    label: string;
    unit: string;
    placeHolder: string;
    dataType: string;
    configuratorProperty: ConfiguratorProperty;
    value: T;
    istext: boolean;
}

export interface CoreProduct {
    articleNumber: string;
    calculatedQuantity: number;
    confirmedQuantity: number;
    added: boolean;
  }

export interface Product extends CoreProduct {
    name: string;
    imageUrl: string;
    productPrice: ProductPrice;
    productSpecification: ProductSpecification;
    itemPriceUnit: string;
    carbonSavingsModel: CarbonSavingsModel;
    showCarbonSavingsInfo: boolean;
    isECOVariantChoosen: boolean;
    changeToPlasterProduct: string;
  }

export interface ProductSpecification {
    title: string;
    description: string;
    imageUrl: string;
    documents: ProductDocument[];
    itemPriceUnit: string;
}

export interface ProductPrice {
    price: number;
    currencyCode: string;
    formattedPrice: string;
}

export interface ProductDocument {
    fileid: string;
    fileName: string;
    url: string;
    downloadUrl: string;
    fileType: string;
 }

export interface ConditionalPropertyValue {
    propertyName: string;
    validPropertyValues: string[];
}

export interface CalculateProductQuantityRequestModel {
    products: CoreProduct[];
    configuratorValues: ConfiguratorValue[];
}

export interface CalculateProductQuantityResponseModel {
    products: CoreProduct[];
    propertyModelValues: PropertyModelValue[];
    changeCalculatedPlaster: boolean;
}

export interface AddToCartRequestModel {
    products: CoreProduct[];
}

export interface TranslationModel {
    nextStepText: string;
    addToCartButtonText: string;
    addedProductButtonText: string;
    addProductButtonText: string;
    showProductSpecificationButtonText: string;
    quantityChangedHeading: string;
    quantityChangedText: string;
    addedToCartHeading: string;
    addedToCartText: string;
    addedToCartContinueToCart: string;
    addedToCartCancel: string;
    commonHeight: string;
    commonLength: string;
    commonMeter: string;
    commonYes: string;
    commonNo: string;
    commonThickness: string;
    wallSectionNamePlaceholder: string;
    heightAboveGround: string;
    heightRetainingWall: string;
    unnamedWallSection: string;
    coping: string;
    toTheLeft: string;
    toTheRight: string;
    selectWallSectionType: string;
    readMore: string;
    readLess: string;
    beam: string;
    beamDescription: string;
    buildingWallOpenings: string;
    buildingWallOpeningsDescription: string;
    addBuildingWallOpenings: string;
    indoorBuildingWallThicknessHeading: string;
    exceedAllowedLimitMessage: string;
    belowAllowedLimitMessage: string;
    notWithinValidIntervalMessage: string;
    invalidBuildingWallOpeningMessage: string;
    gableEnd: string;
    unnamedBuildingWallSection: string;
    unnamedBuildingWallOpening: string;
    commonPiecesAbbreviation: string;
    blockType: string;
    unnamedBathroomWallOpening: string;
    invalidBathroomWallOpeningMessage: string;
    selectedProductButtonText: string;
    selectProductButtonText: string;
    productListCustomText: string;
    basicElementsHeightOptionHeader: string;
    basicElementsHeightOptionText: string;
    basicElementsWidthOptionHeader: string;
    basicElementsWidthOptionText: string;
    resetSelectedCookie: string;
}

export interface VariantHeightOrWeightOptions {
    id: string;
    options: number[];
}
